import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  Stack,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Paper,
  Fade,
  Badge,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Done as DoneIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDispatch, useSelector } from "react-redux";
import {
  setDateRange,
  setSpecies,
  setFilterApplied,
  setTimeRange,
} from "./LiveFilterSlice";
import { selectOrganization } from "../organization/organizationSlice";

export function LiveFilter() {
  const [open, setOpen] = useState(false);
  const { species: availSpecies } = useSelector(selectOrganization);
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(new Date());
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [selectAllSpecies, setSelectAllSpecies] = useState(false);

  const speciesItems = useMemo(() => {
    return (
      availSpecies?.map((v) => ({
        id: v.id,
        name: v.name,
      })) || []
    );
  }, [availSpecies]);

  // Handle select all species
  useEffect(() => {
    if (selectAllSpecies) {
      const allSpeciesIds = speciesItems.map((s) => s.id);
      setSelectedSpecies(allSpeciesIds);
    } else {
      setSelectedSpecies([]);
    }
  }, [selectAllSpecies, speciesItems]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleApplyFilter = () => {
    dispatch(
      setDateRange({
        startDate: startDate,
        endDate: endDate,
        key: "range",
      }),
    );
    dispatch(setSpecies(selectedSpecies));
    dispatch(setFilterApplied(true));
    dispatch(setTimeRange({ startTime: startTime, endTime: endTime }));
    setOpen(false);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(new Date());
    setStartTime(new Date());
    setEndTime(new Date());
    setSelectedSpecies([]);
    setSelectAllSpecies(false);
  };

  const hasActiveFilters = selectedSpecies.length > 0 || startDate !== null;
  const activeFilterCount =
    (selectedSpecies.length > 0 ? 1 : 0) + (startDate !== null ? 1 : 0);

  const styles = {
    filterButton: {
      background:
        "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
      borderRadius: "30px",
      padding: "8px 16px",
      color: "white",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "5px",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
      },
    },
    dialogPaper: {
      borderRadius: "24px",
      background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
      maxWidth: "500px",
      width: "100%",
      overflow: "hidden",
    },
    dialogTitle: {
      background:
        "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
      color: "white",
      padding: "16px 20px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    section: {
      padding: "16px 20px",
      borderBottom: "1px solid #e0e0e0",
    },
    sectionTitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#454545",
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    selectAllBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "12px",
      padding: "6px 10px",
      backgroundColor: "#f5f5f5",
      borderRadius: "10px",
    },
    chipContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: "6px",
      marginTop: "8px",
    },
    chipSelected: {
      background: "linear-gradient(135deg, #454545 0%, #5a5a5a 100%)",
      color: "white",
    },
    chipUnselected: {
      background: "#f0f0f0",
      color: "#666",
    },
    actionButtons: {
      display: "flex",
      gap: "10px",
      padding: "16px 20px",
      justifyContent: "flex-end",
    },
    resetButton: {
      borderRadius: "25px",
      padding: "6px 20px",
      textTransform: "none",
      fontWeight: 600,
      border: "1px solid #ddd",
      color: "#666",
      backgroundColor: "white",
      "&:hover": {
        borderColor: "#454545",
        backgroundColor: "#f5f5f5",
      },
    },
    applyButton: {
      borderRadius: "25px",
      padding: "6px 28px",
      textTransform: "none",
      fontWeight: 600,
      background: "linear-gradient(135deg, #454545 0%, #5a5a5a 100%)",
      color: "white",
      border: "none",
      "&:hover": {
        background: "linear-gradient(135deg, #5a5a5a 0%, #6b6b6b 100%)",
      },
    },
  };

  return (
    <div>
      <button onClick={handleOpen} style={styles.filterButton}>
        <FilterListIcon sx={{ fontSize: 20 }} />
        {hasActiveFilters && (
          <Badge
            badgeContent={activeFilterCount}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#ff4444",
                fontSize: "10px",
                height: "16px",
                minWidth: "16px",
              },
            }}
          />
        )}
      </button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.dialogPaper }}
        TransitionComponent={Fade}
      >
        <Box sx={styles.dialogTitle}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "1.1rem",
            }}
          >
            <FilterListIcon sx={{ fontSize: 22 }} />
            Live Event Filters
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white", p: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Date Range */}
        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>📅 Date Range</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction="row" spacing={1.5}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
                sx={{ flex: 1 }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
                sx={{ flex: 1 }}
              />
            </Stack>
          </LocalizationProvider>
        </Box>

        {/* Time Range */}
        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>⏰ Time Range</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction="row" spacing={1.5}>
              <TimePicker
                label="Start Time"
                value={startTime}
                onChange={setStartTime}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
                sx={{ flex: 1 }}
              />
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={setEndTime}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
                sx={{ flex: 1 }}
              />
            </Stack>
          </LocalizationProvider>
        </Box>

        {/* Species Section */}
        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>🏷️ Species / Tags</Typography>
          <Paper elevation={0} sx={styles.selectAllBox}>
            <FormControlLabel
              control={
                <Switch
                  checked={selectAllSpecies}
                  onChange={(e) => setSelectAllSpecies(e.target.checked)}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#454545",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#454545",
                    },
                  }}
                />
              }
              label="Select All"
            />
            {selectedSpecies.length > 0 && (
              <Chip
                label={`${selectedSpecies.length} selected`}
                size="small"
                sx={{
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                  height: 24,
                  fontSize: "11px",
                }}
              />
            )}
          </Paper>
          <Box sx={styles.chipContainer}>
            {speciesItems.map((species) => (
              <Chip
                key={species.id}
                label={species.name}
                onClick={() => {
                  if (selectedSpecies.includes(species.id)) {
                    setSelectedSpecies(
                      selectedSpecies.filter((id) => id !== species.id),
                    );
                    setSelectAllSpecies(false);
                  } else {
                    setSelectedSpecies([...selectedSpecies, species.id]);
                  }
                }}
                icon={
                  selectedSpecies.includes(species.id) ? (
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                  ) : undefined
                }
                size="small"
                sx={{
                  borderRadius: "20px",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  ...(selectedSpecies.includes(species.id)
                    ? styles.chipSelected
                    : styles.chipUnselected),
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={styles.actionButtons}>
          <Button
            startIcon={<ClearIcon />}
            onClick={handleReset}
            sx={styles.resetButton}
          >
            Reset
          </Button>
          <Button
            startIcon={<DoneIcon />}
            onClick={handleApplyFilter}
            sx={styles.applyButton}
          >
            Apply
          </Button>
        </Box>
      </Dialog>
    </div>
  );
}
